import { createSelector } from "reselect";
import {
  ANALYSIS_REQUEST_NAMESPACE,
  ANALYSIS_REAGENT_PROVIDER_NAMESPACE,
  ANALYSIS_REQUEST_CATEGORY_NAMESPACE,
  ANALYSIS_REQUEST_CODE_NAMESPACE,
} from "../actions/analysis-request";
import { RootState } from "../reducers/root";
import {
  AnalysisReagentProvider,
  AnalysisRequest,
  AnalysisRequestCategory,
  AnalysisRequestCode,
  AnalysisRequestCodeLifecycle,
  AnalysisRequestRef,
  AnalysisRequestState,
} from "../types/shared/v2/AnalysisRequest";
import { ObjectHeader, PrivacyDomain, StoreObject } from "../util/objects";
import { selectObjectByAttr } from "./objects";
import { WorklistV2 } from "../types/shared/v2/Worklist";
import { isSelectableAnalysisState } from "../util/analysis-request";
import { collectPatientInlogIds } from "../util/analysis-request-sync";

export const selectAnalysisRequestIndexedObjects = (
  state: RootState
):
  | { [objectId: string]: StoreObject<AnalysisRequest> | ObjectHeader }
  | undefined => {
  return state.objects.byNamespace[ANALYSIS_REQUEST_NAMESPACE]?.byDomain[
    PrivacyDomain.Public
  ]?.byId;
};

export const selectAnalysisRequestObjects = createSelector(
  [selectAnalysisRequestIndexedObjects],
  (objects) => {
    const analysisRequests: StoreObject<AnalysisRequest>[] = [];
    if (!objects) return analysisRequests;
    return Object.values(objects).reduce((memo, obj) => {
      if (!("data" in obj)) return memo;
      memo.push(obj);
      return memo;
    }, analysisRequests);
  }
);

export const selectAnalysisRequestByRef = createSelector(
  [selectAnalysisRequestObjects, (state: RootState, ref?: AnalysisRequestRef) => ref],
  (objects, ref) => {
    return objects.find(obj => ref?.executorSampleId === obj.data.executorSampleId &&
      ref?.requestDate === obj.data.requestDate &&
      ref?.requestorSampleId === obj.data.requestorSampleId &&
      ref?.samplingStartDate === obj.data.samplingStartDate &&
      ref?.samplingEndDate === obj.data.samplingEndDate &&
      (ref?.type === undefined || Object.keys(obj.data.types).indexOf(ref?.type) !== -1)
    );
  }
);

export const selectAnalysisRequests = createSelector(
  [selectAnalysisRequestObjects],
  (objects) => {
    const analysisRequests: AnalysisRequest[] = [];
    if (!objects) return analysisRequests;
    return objects.map(obj => obj.data)
  }
);

// Patients à synchroniser depuis le tableau de bord des demandes.
export const selectAnalysisRequestPatientInlogIds = createSelector(
  [selectAnalysisRequests],
  (analysisRequests) =>
    collectPatientInlogIds(analysisRequests, { inProgressOnly: false })
);

// Ticket #894 - Patients ayant au moins un examen non soldé : c'est la
// synchronisation courante, l'historique complet restant accessible à part.
export const selectInProgressPatientInlogIds = createSelector(
  [selectAnalysisRequests],
  (analysisRequests) =>
    collectPatientInlogIds(analysisRequests, { inProgressOnly: true })
);

// Demandes ayant au moins un examen sélectionnable pour une nouvelle liste de
// travail : pris en compte (stade 1) ou remis « à refaire » (stade 7, #821).
export const selectTakenIntoAccountAnalysisRequests = createSelector(
  [selectAnalysisRequests],
  (analysisRequests) => {
    return analysisRequests.filter((r) => {
      const analysisRequestCodes = Object.keys(
        r.types
      ) as string[];

      const isPending =
        analysisRequestCodes.some(
          (typ) => isSelectableAnalysisState(r.types[typ])
        )

      return isPending
    })
  }
);
export const selectPendingAnalysisRequests = createSelector(
  [selectAnalysisRequests],
  (analysisRequests) => {
    return analysisRequests.filter((r) => {
      const analysisRequestCodes = Object.keys(
        r.types
      ) as string[];

      const isPending =
        analysisRequestCodes.some(
          (typ) => ([AnalysisRequestState.TakenIntoAccount, AnalysisRequestState.IntegratedWorklist, AnalysisRequestState.RedoRequested].includes(r.types[typ]))
        )

      return isPending
    })
  }
);


export const selectPatientPendingAnalysisRequests = createSelector(
  [
    selectPendingAnalysisRequests,
    (state: RootState, inlogId: string) => inlogId,
  ],
  (pendingAnalyses, inlogId) => {
    const analysisRequest: AnalysisRequest[] = [];
    if (!pendingAnalyses) return analysisRequest;

    return Object.values(pendingAnalyses).reduce((memo, pending) => {
      if (pending.ref.patient.inlogId !== inlogId) return memo;
      memo.push(pending);
      return memo;
    }, analysisRequest);
  }
);

export const selectAnalysisReagentProviderIndexedObjects = (
  state: RootState
):
  | { [objectId: string]: StoreObject<AnalysisReagentProvider> | ObjectHeader }
  | undefined => {
  return state.objects.byNamespace[ANALYSIS_REAGENT_PROVIDER_NAMESPACE]
    ?.byDomain[PrivacyDomain.Public]?.byId;
};

export const selectAnalysisReagentProviderObjects = createSelector(
  [selectAnalysisReagentProviderIndexedObjects],
  (objects) => {
    const items: StoreObject<AnalysisReagentProvider>[] = [];
    if (!objects) return items;

    return Object.values(objects).reduce((memo, obj) => {
      if (!("data" in obj)) return memo;
      memo.push(obj);
      return memo;
    }, items);
  }
);

export const selectAnalysisReagentByCode = createSelector(
  [selectAnalysisReagentProviderObjects],
  (objects) => {
    const analysisReagentByCode = {};
    Object.values(objects).map((analysisReagent) => {
      return analysisReagentByCode[analysisReagent.data.code] = analysisReagent.data.label;
    });
    return analysisReagentByCode as { [key: string]: string }
  }
);
export const selectAnalysisReagentProviders = createSelector(
  [selectAnalysisReagentProviderObjects],
  (objects) => {
    return objects.map((obj) => obj.data);
  }
);

export const selectAnalysisReagentProviderByCode = (
  state: RootState,
  code: string
) => {
  return selectObjectByAttr<AnalysisReagentProvider>(
    state,
    ANALYSIS_REAGENT_PROVIDER_NAMESPACE,
    PrivacyDomain.Public,
    "code",
    code
  );
};

export const selectAnalysisRequestCategoryIndexedObjects = (
  state: RootState
):
  | { [objectId: string]: StoreObject<AnalysisRequestCategory> | ObjectHeader }
  | undefined => {
  return state.objects.byNamespace[ANALYSIS_REQUEST_CATEGORY_NAMESPACE]
    ?.byDomain[PrivacyDomain.Public]?.byId;
};

export const selectAnalysisRequestCategoryObjects = createSelector(
  [selectAnalysisRequestCategoryIndexedObjects],
  (objects) => {
    const items: StoreObject<AnalysisRequestCategory>[] = [];
    if (!objects) return items;

    return Object.values(objects).reduce((memo, obj) => {
      if (!("data" in obj)) return memo;
      memo.push(obj);
      return memo;
    }, items);
  }
);

export const selectAnalysisRequestCategories = createSelector(
  [selectAnalysisRequestCategoryObjects],
  (objects) => {
    return objects.map((obj) => obj.data)
      .sort((arc1, arc2) => {
        if (arc1.code > arc2.code) return 1;
        if (arc1.code < arc2.code) return -1;
        return 0;
      });
  }
);

export const selectAnalysisRequestCategoriesIndexedByCode = createSelector(
  [selectAnalysisRequestCategoryObjects],
  (objects) => {
    const analysisRequestCategoriesByCode = {};
    objects.map((category) => {
      return analysisRequestCategoriesByCode[category.data.code] = category.data.label;
    });
    return analysisRequestCategoriesByCode as { [key: string]: string }
  }
);

export const selectAnalysisRequestCategoriesByReagent = createSelector([
  (state: RootState, reagentProviderCode: string) => selectAnalysisRequestCodeByReagent(state, reagentProviderCode),
  selectAnalysisRequestCategories,
], (analysisRequestCodes, analysisRequestCategories) => {
  return analysisRequestCategories
    .filter(arc => analysisRequestCodes?.some(analysisRequestCode => analysisRequestCode.ref.analysisRequestCategory.code === arc.code))
})

export const selectAnalysisRequestCategoryByCode = (
  state: RootState,
  code: string
) => {
  return selectObjectByAttr<AnalysisRequestCategory>(
    state,
    ANALYSIS_REQUEST_CATEGORY_NAMESPACE,
    PrivacyDomain.Public,
    "code",
    code
  );
};

export const selectAnalysisRequestCodeIndexedObjects = (
  state: RootState
):
  | { [objectId: string]: StoreObject<AnalysisRequestCode> | ObjectHeader }
  | undefined => {
  return state.objects.byNamespace[ANALYSIS_REQUEST_CODE_NAMESPACE]?.byDomain[
    PrivacyDomain.Public
  ]?.byId;
};

export const selectAnalysisRequestCodeObjects = createSelector(
  [selectAnalysisRequestCodeIndexedObjects],
  (objects) => {
    const items: StoreObject<AnalysisRequestCode>[] = [];
    if (!objects) return items;
    return Object.values(objects).reduce((memo, obj) => {
      if (!("data" in obj)) return memo;
      memo.push(obj);
      return memo;
    }, items);
  }
);


export const selectAnalysisRequestCodeSynthesis = (
  state: RootState,
): string[] | undefined => {
  const objectsById = state.objects.byNamespace[ANALYSIS_REQUEST_CODE_NAMESPACE]?.byDomain[
    PrivacyDomain.Public
  ]?.byId;

  if (objectsById) {
    const items: StoreObject<AnalysisRequestCode>[] = [];
    Object.values(objectsById).reduce((memo, obj) => {
      if (!("data" in obj)) return memo;
      memo.push(obj);
      return memo;
    }, items);
    const objectsFilter = Object.values(items).filter((obj) => {
      return obj.data.lifecycle === AnalysisRequestCodeLifecycle.Synthesis;
    });

    const analysisRequestCode: string[] = [];
    return Object.values(objectsFilter).reduce((memo, obj) => {
      if (!("data" in obj)) return memo;
      memo.push(obj.data.code);
      return memo;
    }, analysisRequestCode);
  }
  return undefined;
};

export const selectAnalysisRequestCodeByReagent = createSelector(
  [
    selectAnalysisRequestCodeObjects,
    (state: RootState, reagentProviderCode: string) => reagentProviderCode
  ],
  (analysisRequestCodes, reagentProviderCode) => {
    return analysisRequestCodes.reduce((memo, obj) => {
      if (!("data" in obj)) return memo;
      if (obj.data.ref.analysisReagentProvider.code === reagentProviderCode || !reagentProviderCode) {
        memo.push(obj.data);
      }
      return memo;
    }, [] as AnalysisRequestCode[]);

  }
)

// Mémoïsée pour conserver une référence stable du tableau retourné tant que
// les codes d'examen et le fournisseur/catégorie sélectionnés ne changent pas
// (sinon, un consommateur utilisant ce résultat comme dépendance de useEffect
// le ré-exécute en boucle à chaque rendu)
export const selectAnalysisRequestCodeByReagentAndCategory = createSelector(
  [
    selectAnalysisRequestCodeIndexedObjects,
    (state: RootState, reagent: string | null) => reagent ? selectAnalysisRequestCodeByReagent(state, reagent) : undefined,
    (_state: RootState, _reagent: string | null, category: string | null) => category,
  ],
  (objectsById, analysisRequestCodes, category): AnalysisRequestCode[] | undefined => {
    if (!objectsById || !analysisRequestCodes) return undefined;
    if (category && category.length > 0) {
      return analysisRequestCodes.filter((code) => code.ref.analysisRequestCategory.code === category);
    }
    return analysisRequestCodes;
  }
);

export const selectPendingAnalysisObjectsRequests = createSelector(
  [selectAnalysisRequestObjects],
  (analysisRequests) => {
    const analysisRequest: StoreObject<AnalysisRequest>[] = [];
    return analysisRequests.reduce((memo, obj) => {
      const analysisRequestCodes = Object.keys(
        obj.data.types
      ) as string[];
      const isPending =
        analysisRequestCodes.some(
          (code) => isSelectableAnalysisState(obj.data.types[code])
        )
      if (!isPending) return memo;

      memo.push(obj);

      return memo;
    }, analysisRequest);
  }
);

export const selectPendingAnalysisRequestsByReagent = createSelector(
  [
    selectTakenIntoAccountAnalysisRequests,
    (state: RootState, reagentProvider: string) => reagentProvider,
    selectAnalysisRequestCodeByReagent,
  ],
  (pendingAnalysisRequests, reagentProvider, analysisRequestCodes) => {
    return pendingAnalysisRequests.filter(analysisRequest => {
      return Object.keys(analysisRequest.types).some(code => {
        const analysisRequestCode = analysisRequestCodes?.find(arc => arc.code === code);
        if (!analysisRequestCode) return false;
        return analysisRequestCode.ref.analysisReagentProvider.code === reagentProvider
      })
    })
  }
)

export const selectAnalysisRequestByWorklist = createSelector(
  [
    selectAnalysisRequests,
    (state: RootState, worklist: WorklistV2) => worklist
  ],
  (analysisRequests, worklist) => {
    if (!analysisRequests) return [];

    const filtered = new Set<AnalysisRequest>();

    worklist.selections.forEach(s => {
      const found = analysisRequests.find(r => {
        const matches = s.ref.analysisRequest.executorSampleId === r.executorSampleId &&
          s.ref.analysisRequest.requestorSampleId === r.requestorSampleId &&
          s.ref.analysisRequest.requestDate === r.requestDate &&
          s.ref.analysisRequest.samplingEndDate === r.samplingEndDate &&
          s.ref.analysisRequest.samplingStartDate === r.samplingStartDate

        return matches
      })

      if (!found) return;

      filtered.add(found);
    })

    return Array.from(filtered);
  }
)

export const selectHandledAnalysisRequestObjects = createSelector(
  [selectAnalysisRequestObjects, selectAnalysisRequestCodeObjects],
  (analysisRequests, requestCodes) => {

    const codes = new Set<string>();
    requestCodes.forEach(rc => codes.add(rc.data.code));

    return analysisRequests.filter(ar => {
      const isHandled = Object.keys(ar.data.types).some(c => codes.has(c) && ar.data.types[c] !== AnalysisRequestState.Historized)
      return isHandled;
    })
  }
);
